import React, { useState, useMemo, useEffect } from "react";
import { PiShoppingBag } from "react-icons/pi";
import { MdAccessTime } from "react-icons/md";
import { FaRupeeSign } from "react-icons/fa";
import { FiDownloadCloud, FiChevronDown, FiChevronUp } from "react-icons/fi";
import moment from "moment";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import {
    getPartner,
    getPartnerDetails,
    getPartnerView,
} from "../../../../Service/partnerAPI";
import Loader from "../../../../reusableUI/loader";
import { topBarImages } from "../../../../reusableUI/images";

/* ---------------- formatting helpers ---------------- */

const formatCurrency = (value) =>
    `₹${(Number(value) || 0).toLocaleString("en-IN", {
        maximumFractionDigits: 2,
    })}`;

const formatCurrencyPdf = (value) =>
    `Rs ${(Number(value) || 0).toLocaleString("en-IN", {
        maximumFractionDigits: 2,
    })}`;

const formatDate = (dateStr) =>
    dateStr ? moment(dateStr).format("D MMM, YYYY") : "-";

const formatDateRange = (from, to) => {
    if (!from) return "-";
    if (!to || from === to) return formatDate(from);
    return `${formatDate(from)} - ${formatDate(to)}`;
};

const formatTimeRange = (time) => time || "-";

/* ---------------- ✅ STATUS HELPERS (ONLY ON-GOING + COMPLETED) ---------------- */

const normalizeStatusKey = (status) =>
    String(status || "")
        .trim()
        .toLowerCase()
        .replace(/[\s_-]+/g, "");

const isOngoingStatus = (status) => {
    const s = normalizeStatusKey(status);
    return s === "accepted" || s === "startduty" || s === "ongoing";
};

const isCompletedStatus = (status) => {
    const s = normalizeStatusKey(status);
    return s === "completed" || s === "endduty";
};

/**
 * ✅ Only allow on-going + completed rows in UI (including "All")
 * If you want to ALSO include others later, change this predicate.
 */
const isEligibleVisitStatus = (status) =>
    isOngoingStatus(status) || isCompletedStatus(status);

/* ---------------- payout transaction helpers ---------------- */

const getSettledDutyTransactions = (dutyTransactions = []) =>
    dutyTransactions.filter((dt) => {
        const s = (dt.status || "").toLowerCase();
        return s === "processed" || s === "completed";
    });

const getServicesList = (payout) => {
    if (Array.isArray(payout?.services) && payout.services.length) {
        return Array.from(
            new Set(
                payout.services
                    .filter((x) => typeof x === "string")
                    .map((x) => x.trim())
                    .filter(Boolean)
            )
        );
    }

    const set = new Set();
    (payout?.dutyTransactions || []).forEach((dt) => {
        (dt?.services || []).forEach((s) => {
            if (typeof s === "string" && s.trim()) set.add(s.trim());
            else if (s?.name) set.add(String(s.name).trim());
        });
    });

    return Array.from(set);
};

/* ---------------- UI ---------------- */

const SummaryCard = ({ title, amount, subtitle, icon, accent }) => (
    <div className="bg-white rounded-[12px] shadow-sm border border-slate-100 px-4 py-6 flex items-center justify-between">
        <div className="flex flex-col">
            <p className="text-xs font-medium text-slate-500 font-poppins">
                {title}
            </p>
            <p className="mt-1 text-2xl font-semibold text-slate-900 font-poppins">
                {formatCurrency(amount)}
            </p>
            {subtitle ? (
                <p className="mt-1 text-[11px] text-emerald-500 font-poppins">
                    {subtitle}
                </p>
            ) : null}
        </div>

        <div
            className={`w-10 h-10 rounded-2xl flex items-center justify-center text-[18px] ${accent}`}
        >
            {icon}
        </div>
    </div>
);

const StatusPill = ({ status }) => {
    const s = normalizeStatusKey(status);

    // ✅ only ongoing/completed styles
    if (s === "accepted" || s === "startduty" || s === "ongoing") {
        return (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-[12px] text-[11px] font-poppins font-medium bg-blue-50 text-blue-600">
                <span className="w-1.5 h-1.5 rounded-full bg-current" />
                On-Going
            </span>
        );
    }

    if (s === "completed" || s === "endduty") {
        return (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-[12px] text-[11px] font-poppins font-medium bg-emerald-50 text-emerald-600">
                <span className="w-1.5 h-1.5 rounded-full bg-current" />
                Completed
            </span>
        );
    }

    // Should not appear because we filter them out, but safe fallback:
    return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-[12px] text-[11px] font-poppins font-medium bg-slate-100 text-slate-600">
            <span className="w-1.5 h-1.5 rounded-full bg-current" />
            {status || "Unknown"}
        </span>
    );
};

const DayStatusPill = ({ status }) => {
    const s = (status || "").toLowerCase();
    let text = status || "";
    let cls = "bg-slate-50 text-slate-600 border-slate-100";

    if (s === "processed" || s === "completed") {
        cls = "bg-emerald-50 text-emerald-600 border-emerald-100";
        text = s === "processed" ? "Processed" : "Completed";
    } else if (s === "pending" || s === "payment pending") {
        // (Duty pending is still possible at transaction level)
        cls = "bg-amber-50 text-amber-600 border-amber-100";
        text = "Pending";
    }

    return (
        <span
            className={`inline-flex items-center px-2 py-0.5 rounded-[12px] text-[11px] font-medium font-poppins border ${cls}`}
        >
            {text}
        </span>
    );
};

const BreakdownTable = ({ dutyTransactions, totalDays }) => {
    const totalDailyFee = dutyTransactions.reduce(
        (sum, d) => sum + (d.dailyFee || 0),
        0
    );
    const totalPenalty = dutyTransactions.reduce(
        (sum, d) => sum + (d.penalty || 0),
        0
    );
    const totalPlatformFee = 0;

    const totalAmount = dutyTransactions.reduce((sum, d) => {
        if (d.amountToTransfer != null) return sum + d.amountToTransfer;
        return sum + (d.dailyFee || 0) - (d.penalty || 0) + (d.bonus || 0);
    }, 0);

    return (
        <div className="w-full overflow-x-auto">
            <table className="w-full min-w-[920px] text-[11px] sm:text-[12px] bg-white rounded-[6px] overflow-hidden text-center">
                <thead className="bg-slate-100">
                    <tr className="text-slate-500 font-poppins">
                        <th className="py-2 px-3 font-medium text-center">Day</th>
                        <th className="py-2 px-3 font-medium text-center">Date</th>
                        <th className="py-2 px-3 font-medium text-center">Attended By</th>
                        <th className="py-2 px-3 font-medium text-center">Daily Fee</th>
                        <th className="py-2 px-3 font-medium text-center">Penalty</th>
                        <th className="py-2 px-3 font-medium text-center">Platform Fee</th>
                        <th className="py-2 px-3 font-medium text-center">Status</th>
                        <th className="py-2 px-3 font-medium text-center">Amount</th>
                    </tr>
                </thead>

                <tbody>
                    {dutyTransactions.map((d, idx) => {
                        const amount =
                            d.amountToTransfer != null
                                ? d.amountToTransfer
                                : (d.dailyFee || 0) - (d.penalty || 0) + (d.bonus || 0);

                        return (
                            <tr key={idx} className="border-t border-slate-100 font-poppins">
                                <td className="py-2 px-3 align-top text-center">
                                    <div className="text-[12px] font-medium text-slate-900">
                                        {d.day || `Day ${idx + 1}/${totalDays}`}
                                    </div>
                                </td>

                                <td className="py-2 px-3 align-top text-center text-slate-700">
                                    {d.date
                                        ? moment(d.date, "YYYY-MM-DD").format("D MMM, YYYY")
                                        : "-"}
                                </td>

                                <td className="py-2 px-3 align-top text-center text-slate-700">
                                    {d.attendedBy || "-"}
                                </td>

                                <td className="py-2 px-3 align-top text-center text-slate-700">
                                    {formatCurrency(d.dailyFee)}
                                </td>

                                <td className="py-2 px-3 align-top text-center text-slate-700">
                                    {formatCurrency(d.penalty)}
                                </td>

                                <td className="py-2 px-3 align-top text-center text-slate-700">
                                    {formatCurrency(0)}
                                </td>

                                <td className="py-2 px-3 align-top text-center">
                                    <DayStatusPill status={d.status} />
                                </td>

                                <td className="py-2 px-3 align-top text-center font-semibold text-slate-900">
                                    {formatCurrency(amount)}
                                </td>
                            </tr>
                        );
                    })}

                    <tr className="bg-slate-50 border-t border-slate-200">
                        <td className="py-2 px-3 text-[12px] font-semibold text-slate-900 font-poppins text-center pr-4">
                            <div className="flex flex-col items-center leading-tight">
                                <span>Total</span>
                            </div>
                        </td>
                        <td />
                        <td />
                        <td className="px-3 text-[12px] text-center text-slate-700">
                            {formatCurrency(totalDailyFee)}
                        </td>
                        <td className="px-3 text-[12px] text-center text-slate-700">
                            {formatCurrency(totalPenalty)}
                        </td>
                        <td className="px-3 text-[12px] text-center text-slate-700">
                            {formatCurrency(totalPlatformFee)}
                        </td>
                        <td />
                        <td className="px-3 text-[12px] text-center font-semibold text-slate-900">
                            {formatCurrency(totalAmount)}
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>
    );
};

const PayoutCard = ({ payout, expanded, onToggle }) => {
    const {
        jobID,
        status,
        patientName,
        visitAddress,
        fromDate,
        toDate,
        time,
        dutyTransactions = [],
    } = payout;

    const settledDutyTransactions = getSettledDutyTransactions(dutyTransactions);

    const computedTotalAmount = settledDutyTransactions.reduce((sum, d) => {
        if (d.amountToTransfer != null) return sum + d.amountToTransfer;
        return sum + (d.dailyFee || 0) - (d.penalty || 0) + (d.bonus || 0);
    }, 0);

    const avatarInitials = (patientName || "")
        .split(" ")
        .filter(Boolean)
        .map((n) => n[0])
        .join("");

    const servicesList = getServicesList(payout);

    return (
        <div className="bg-white rounded-[12px] border border-slate-200 shadow-sm overflow-hidden">
            <div
                className="px-4 py-3 flex flex-col gap-3 cursor-pointer hover:bg-slate-50"
                onClick={onToggle}
            >
                <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-3">
                    <div className="flex flex-col gap-1">
                        <div className="flex items-center gap-2">
                            <span className="text-[12px] font-semibold text-slate-900 font-poppins">
                                {jobID}
                            </span>
                            <StatusPill status={status} />
                        </div>

                        <div className="flex items-center gap-2 mt-1">
                            <div className="w-8 h-8 rounded-full bg-indigo-100 flex items-center justify-center text-[11px] font-semibold text-indigo-700 font-poppins">
                                {avatarInitials}
                            </div>

                            <div className="min-w-0">
                                <p className="text-[12px] font-medium text-slate-900 font-poppins truncate">
                                    {patientName}
                                </p>
                                <p className="text-[11px] text-slate-500 font-poppins truncate">
                                    {visitAddress || "-"}
                                </p>
                            </div>
                        </div>
                    </div>

                    <div className="w-full md:w-auto md:flex-1 md:max-w-[720px]">
                        <div className="grid grid-cols-2 sm:grid-cols-4 items-start gap-x-8 gap-y-3 text-[11px] text-slate-500">
                            <div className="text-left min-w-0">
                                <p className="font-poppins">Visit Dates</p>
                                <p className="mt-1 text-[12px] font-medium text-slate-900 font-poppins break-words">
                                    {formatDateRange(fromDate, toDate)}
                                </p>
                            </div>

                            <div className="text-left min-w-0">
                                <p className="font-poppins">Time</p>
                                <p className="mt-1 text-[12px] font-medium text-slate-900 font-poppins break-words">
                                    {formatTimeRange(time)}
                                </p>
                            </div>

                            <div className="text-left min-w-0">
                                <p className="font-poppins">Services</p>
                                {servicesList.length ? (
                                    <ul className="mt-1 text-[12px] font-medium text-slate-900 font-poppins list-decimal list-inside space-y-0.5">
                                        {servicesList.map((service, idx) => (
                                            <li
                                                key={`${service}-${idx}`}
                                                className="break-words leading-4"
                                            >
                                                {service}
                                            </li>
                                        ))}
                                    </ul>
                                ) : (
                                    <p className="mt-1 text-[12px] font-medium text-slate-900 font-poppins">
                                        -
                                    </p>
                                )}
                            </div>

                            <div className="text-left min-w-0">
                                <p className="font-poppins">Total Amount</p>
                                <p className="mt-1 text-[12px] font-medium text-slate-900 font-poppins break-words">
                                    {formatCurrency(computedTotalAmount)}
                                </p>
                            </div>
                        </div>
                    </div>

                    <div className="flex md:flex-col items-start md:items-end justify-between md:justify-start gap-2">
                        <div className="flex items-center gap-1 text-[11px] text-indigo-600 font-poppins font-medium select-none">
                            <span>{expanded ? "Hide breakdown" : "View breakdown"}</span>
                            {expanded ? <FiChevronUp size={12} /> : <FiChevronDown size={12} />}
                        </div>
                    </div>
                </div>
            </div>

            <div className="px-4 pb-3 pt-2 bg-slate-50/60 border-t border-slate-100">
                {expanded && settledDutyTransactions.length > 0 && (
                    <BreakdownTable
                        dutyTransactions={settledDutyTransactions}
                        totalDays={settledDutyTransactions.length}
                    />
                )}

                {expanded && settledDutyTransactions.length === 0 && (
                    <div className="py-3 text-[12px] text-slate-500 font-poppins text-left">
                        No processed/completed payout entries for this duty.
                    </div>
                )}
            </div>
        </div>
    );
};

/* ---------------- PDF ---------------- */

const generateStatementPdf = ({ from, to, payouts, partnerInfo }) => {
    const doc = new jsPDF({ orientation: "p", unit: "mm", format: "a4" });
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const marginX = 14;
    const contentWidth = pageWidth - marginX * 2;

    const partnerDisplayName =
        partnerInfo?.basicInfo?.name ||
        partnerInfo?.name ||
        partnerInfo?.partnerName ||
        "Partner";

    const referralCode = partnerInfo?.referralCode || "";
    const email =
        partnerInfo?.email ||
        partnerInfo?.contactInfo?.[0]?.email ||
        partnerInfo?.userEmail ||
        "";
    const phone =
        partnerInfo?.phone ||
        partnerInfo?.contactInfo?.[0]?.phone ||
        partnerInfo?.mobile ||
        partnerInfo?.contactNumber ||
        "";

    const primary = { r: 67, g: 97, b: 238 };
    const textDark = 34;

    doc.setFillColor(primary.r, primary.g, primary.b);
    doc.rect(0, 0, pageWidth, 24, "F");

    doc.setTextColor(255, 255, 255);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(13);
    doc.text("Payout Statement", marginX, 10);

    doc.setFontSize(9);
    doc.setFont("helvetica", "normal");
    doc.text(partnerDisplayName, marginX, 16);

    const periodStr = `${from.format("DD MMM YYYY")} - ${to.format("DD MMM YYYY")}`;
    doc.text(periodStr, pageWidth - marginX, 10, { align: "right" });

    let y = 28;
    doc.setTextColor(textDark);
    doc.setFontSize(9);

    doc.setFillColor(248, 249, 252);
    doc.setDrawColor(222, 226, 230);
    doc.rect(marginX, y, contentWidth, 22, "FD");

    y += 6;
    doc.text(`Partner Name: ${partnerDisplayName}`, marginX + 3, y);
    y += 4;
    doc.text(`Referral Code: ${referralCode || "-"}`, marginX + 3, y);

    const rightColX = marginX + contentWidth / 2;
    let yRight = 34;
    if (phone) {
        doc.text(`Phone: ${phone}`, rightColX, yRight);
        yRight += 4;
    }
    if (email) {
        doc.text(`Email: ${email}`, rightColX, yRight);
    }

    y = 28 + 22 + 6;

    const totalAmount = payouts.reduce((sum, p) => {
        const settledDts = getSettledDutyTransactions(p.dutyTransactions || []);
        const dutyAmount = settledDts.reduce((s, d) => {
            if (d.amountToTransfer != null) return s + d.amountToTransfer;
            return s + (d.dailyFee || 0) - (d.penalty || 0) + (d.bonus || 0);
        }, 0);
        return sum + dutyAmount;
    }, 0);

    const totalVisits = payouts.length;
    const totalDutyDays = payouts.reduce((sum, p) => {
        const settledDts = getSettledDutyTransactions(p.dutyTransactions || []);
        return sum + settledDts.length;
    }, 0);

    doc.setFont("helvetica", "bold");
    doc.text("Summary", marginX, y);
    y += 6;

    autoTable(doc, {
        startY: y,
        head: [["Total Amount", "Total Visits", "Total Duty Days"]],
        body: [[formatCurrencyPdf(totalAmount), String(totalVisits), String(totalDutyDays)]],
        theme: "grid",
        styles: { halign: "center", fontSize: 9 },
        headStyles: { fillColor: [240, 242, 245], textColor: 33 },
        margin: { left: marginX, right: marginX },
    });

    y = doc.lastAutoTable.finalY + 8;

    const rows = [];
    payouts.forEach((p) => {
        const { jobID, patientName, specialistName, status, fromDate, toDate, dutyTransactions } = p;

        const settledDts = getSettledDutyTransactions(dutyTransactions || []);
        const payoutAmount = settledDts.reduce((s, d) => {
            if (d.amountToTransfer != null) return s + d.amountToTransfer;
            return s + (d.dailyFee || 0) - (d.penalty || 0) + (d.bonus || 0);
        }, 0);

        let dutyDateRange = "-";
        if (fromDate && toDate) {
            const fromMoment = moment(fromDate);
            const toMoment = moment(toDate);
            if (fromMoment.isValid() && toMoment.isValid()) {
                if (fromMoment.year() === toMoment.year()) {
                    dutyDateRange = `${fromMoment.format("DD MMM")} - ${toMoment.format("DD MMM, YYYY")}`;
                } else {
                    dutyDateRange = `${fromMoment.format("DD MMM YYYY")} - ${toMoment.format("DD MMM YYYY")}`;
                }
            }
        }

        rows.push([
            dutyDateRange,
            jobID || "-",
            patientName || "-",
            specialistName || "-",
            String(settledDts.length),
            formatCurrencyPdf(payoutAmount),
            (status || "").toUpperCase(),
        ]);
    });

    autoTable(doc, {
        startY: y,
        head: [["Duty Dates", "Ceviliyar ID", "Patient", "HCP", "Days", "Amount", "Status"]],
        body: rows,
        theme: "grid",
        styles: { fontSize: 9, halign: "center", valign: "middle" },
        headStyles: { fillColor: [67, 97, 238], textColor: 255, fontStyle: "bold" },
        alternateRowStyles: { fillColor: [248, 249, 252] },
        margin: { left: marginX, right: marginX },
    });

    const pageCount = doc.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i);
        doc.setFontSize(8);
        doc.setTextColor(150);
        doc.text(`Page ${i} of ${pageCount}`, pageWidth - marginX, pageHeight - 8, { align: "right" });
    }

    const fileName = `${partnerDisplayName}_${from.format("YYYYMMDD")}_${to.format("YYYYMMDD")}.pdf`;
    doc.save(fileName);
};

const Payouts = ({ partnerID, metricsData, transactionsData, partnerInfoData }) => {
    const [partnerInfo, setPartnerInfo] = useState(null);
    const [metrics, setMetrics] = useState(null);
    const [transactions, setTransactions] = useState([]);

    const [search, setSearch] = useState("");
    // ✅ only ongoing + completed filters
    const [statusFilter, setStatusFilter] = useState("all");
    const [timeFilter, setTimeFilter] = useState("all");
    const [expandedId, setExpandedId] = useState(null);

    const [loading, setLoading] = useState(true);

    const [showStatementModal, setShowStatementModal] = useState(false);
    const [statementType, setStatementType] = useState("last10");
    const [customFrom, setCustomFrom] = useState("");
    const [customTo, setCustomTo] = useState("");
    const [isGenerating, setIsGenerating] = useState(false);

    // ✅ If both are provided, treat as injected and skip API
    const isInjected = metricsData !== undefined && transactionsData !== undefined;

    useEffect(() => {
        let cancelled = false;

        const init = async () => {
            setLoading(true);
            try {
                // ✅ NO API CALL when injected
                if (isInjected) {
                    if (cancelled) return;
                    setPartnerInfo(partnerInfoData ?? null);
                    setMetrics(metricsData ?? null);
                    setTransactions(Array.isArray(transactionsData) ? transactionsData : []);
                    return;
                }

                // Otherwise fetch (old behavior)
                const resp = partnerID ? await getPartnerView(partnerID) : await getPartner();
                if (cancelled) return;

                if (resp?.error === false && resp?.data?._id) {
                    const partner = resp.data;
                    setPartnerInfo(partner);

                    const detailsRes = await getPartnerDetails(partner._id);
                    if (cancelled) return;

                    if (detailsRes?.error === false && detailsRes?.data) {
                        setMetrics(detailsRes.data.metrics ?? null);
                        setTransactions(Array.isArray(detailsRes.data.transactions) ? detailsRes.data.transactions : []);
                    } else {
                        setMetrics(null);
                        setTransactions([]);
                    }
                } else {
                    setPartnerInfo(null);
                    setMetrics(null);
                    setTransactions([]);
                }
            } catch (err) {
                console.error("Error fetching partner data:", err);
                if (!cancelled) {
                    setPartnerInfo(null);
                    setMetrics(null);
                    setTransactions([]);
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        };

        init();

        return () => {
            cancelled = true;
        };
    }, [partnerID, isInjected, metricsData, transactionsData, partnerInfoData]);

    const totalEarnings = metrics?.totalEarning || 0;
    const pendingAmount = metrics?.pendingAmount || 0;
    const paidAmount = Math.max(totalEarnings - pendingAmount, 0);

    /**
     * ✅ Eligible transactions = only On-Going + Completed
     * - This affects list, filters, and statement generation.
     */
    const eligibleTransactions = useMemo(() => {
        const list = Array.isArray(transactions) ? transactions : [];
        return list.filter((p) => isEligibleVisitStatus(p?.status));
    }, [transactions]);

    const filteredPayouts = useMemo(() => {
        let list = [...eligibleTransactions];

        if (search.trim()) {
            const q = search.toLowerCase();
            list = list.filter(
                (p) =>
                    (p.jobID || "").toLowerCase().includes(q) ||
                    (p.patientName || "").toLowerCase().includes(q) ||
                    (p.specialistName || "").toLowerCase().includes(q)
            );
        }

        if (statusFilter !== "all") {
            list = list.filter((p) => {
                if (statusFilter === "on-going") return isOngoingStatus(p.status);
                if (statusFilter === "completed") return isCompletedStatus(p.status);
                return true;
            });
        }

        if (timeFilter !== "all") {
            const today = moment().startOf("day");

            list = list.filter((p) => {
                const dates = (p.dutyTransactions || [])
                    .map((dt) => (dt.date ? moment(dt.date, "YYYY-MM-DD").startOf("day") : null))
                    .filter((d) => d && d.isValid());

                if (!dates.length) return false;

                if (timeFilter === "today") return dates.some((d) => d.isSame(today, "day"));

                let from;
                if (timeFilter === "last2") from = today.clone().subtract(1, "day");
                else if (timeFilter === "last7") from = today.clone().subtract(6, "day");
                else if (timeFilter === "last30") from = today.clone().subtract(29, "day");
                else return true;

                return dates.some((d) => d.isBetween(from, today, "day", "[]"));
            });
        }

        return list;
    }, [eligibleTransactions, transactions, search, statusFilter, timeFilter]);

    const totalVisitsEligible = eligibleTransactions.length;

    const handleDownloadStatement = () => {
        if (!eligibleTransactions.length) {
            alert("No eligible payout data (On-Going/Completed) available to generate statement.");
            return;
        }

        const today = moment().startOf("day");
        let from;
        let to = today.clone().endOf("day");

        if (statementType === "last10") from = today.clone().subtract(9, "days").startOf("day");
        else if (statementType === "last1m") from = today.clone().subtract(1, "month").startOf("day");
        else if (statementType === "last3m") from = today.clone().subtract(3, "months").startOf("day");
        else if (statementType === "last6m") from = today.clone().subtract(6, "months").startOf("day");
        else if (statementType === "overall") {
            const allDates = [];
            eligibleTransactions.forEach((p) => {
                const settled = getSettledDutyTransactions(p.dutyTransactions || []);
                settled.forEach((dt) => {
                    if (dt.date) {
                        const d = moment(dt.date, "YYYY-MM-DD").startOf("day");
                        if (d.isValid()) allDates.push(d);
                    }
                });
            });
            if (!allDates.length) {
                alert("No dated processed/completed payout data available.");
                return;
            }
            allDates.sort((a, b) => a.valueOf() - b.valueOf());
            from = allDates[0].clone().startOf("day");
            to = allDates[allDates.length - 1].clone().endOf("day");
        } else if (statementType === "custom") {
            if (!customFrom || !customTo) {
                alert("Please select both From and To dates.");
                return;
            }
            from = moment(customFrom, "YYYY-MM-DD").startOf("day");
            to = moment(customTo, "YYYY-MM-DD").endOf("day");
            if (!from.isValid() || !to.isValid()) {
                alert("Invalid custom date range.");
                return;
            }
            if (from.isAfter(to, "day")) {
                alert("From date cannot be after To date.");
                return;
            }
        } else return;

        const payoutsInRange = eligibleTransactions.filter((p) => {
            const settled = getSettledDutyTransactions(p.dutyTransactions || []);
            const dates = settled
                .map((dt) => (dt.date ? moment(dt.date, "YYYY-MM-DD").startOf("day") : null))
                .filter((d) => d && d.isValid());

            if (!dates.length) return false;
            return dates.some((d) => d.isBetween(from, to, "day", "[]"));
        });

        if (!payoutsInRange.length) {
            alert("No payouts found in the selected period.");
            return;
        }

        try {
            setIsGenerating(true);
            generateStatementPdf({ from, to, payouts: payoutsInRange, partnerInfo });
            setShowStatementModal(false);
        } catch (err) {
            console.error("Error generating PDF:", err);
            alert("Failed to generate statement. Please try again.");
        } finally {
            setIsGenerating(false);
        }
    };

    return (
        <div className="bg-[#f3f5fb] rounded-[12px] w-full h-[90vh] flex flex-col gap-4 overflow-hidden p-2 relative">
            {(loading || isGenerating) && (
                <div>
                    <Loader loading={loading || isGenerating} />
                </div>
            )}

            <div className="flex justify-between items-center">
                <div>
                    <h1 className="text-lg font-semibold text-slate-900 font-poppins">
                        Track and manage
                    </h1>
                </div>

                <button
                    className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full border border-slate-200 bg-white text-[12px] text-slate-700 hover:bg-slate-50"
                    onClick={() => setShowStatementModal(true)}
                    disabled={loading}
                >
                    <FiDownloadCloud size={14} />
                    <span className="font-poppins">Download Statement</span>
                </button>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <SummaryCard
                    title="Total Earnings"
                    amount={totalEarnings}
                    subtitle=""
                    icon={<FaRupeeSign />}
                    accent="bg-indigo-50 text-indigo-600"
                />
                <SummaryCard
                    title="Paid Amount"
                    amount={paidAmount}
                    subtitle=""
                    icon={<PiShoppingBag />}
                    accent="bg-emerald-50 text-emerald-500"
                />
                <SummaryCard
                    title="Pending Amount"
                    amount={pendingAmount}
                    subtitle=""
                    icon={<MdAccessTime />}
                    accent="bg-amber-50 text-amber-500"
                />
            </div>

            <div className="flex items-center justify-between mt-1">
                <p className="text-[13px] font-semibold text-slate-800 font-poppins">
                    Patient Transaction History
                </p>
                <p className="text-[11px] text-slate-500 font-poppins">
                    {totalVisitsEligible} visits
                </p>
            </div>

            <div className="flex flex-col md:flex-row gap-3 items-center">
                <div className="relative flex-1 w-full">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-[13px]">
                        <img src={topBarImages.search} className="h-4 w-4" alt="Search Icon" />
                    </span>

                    <input
                        type="text"
                        placeholder="Search by Ceviliyar ID, Patient Name, HCP Name or Category..."
                        className="w-full rounded-[12px] border border-slate-200 bg-white pl-8 pr-3 py-2 text-[12px] font-poppins outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-400"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        disabled={loading}
                    />
                </div>

                {/* ✅ Only On-Going + Completed */}
                <select
                    className="w-full md:w-44 rounded-[12px] border border-slate-200 bg-white px-3 py-2 text-[12px] font-poppins outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-400"
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    disabled={loading}
                >
                    <option value="all">All </option>
                    <option value="on-going">On-Going</option>
                    <option value="completed">Completed</option>
                </select>

                <select
                    className="w-full md:w-40 rounded-[12px] border border-slate-200 bg-white px-3 py-2 text-[12px] font-poppins outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-400"
                    value={timeFilter}
                    onChange={(e) => setTimeFilter(e.target.value)}
                    disabled={loading}
                >
                    <option value="today">Today</option>
                    <option value="last2">Last 2 Days</option>
                    <option value="last7">Last 7 Days</option>
                    <option value="last30">Last 30 Days</option>
                    <option value="all">Overall</option>
                </select>
            </div>

            <div className="flex-1 overflow-y-auto mt-2 space-y-3 pr-1">
                {filteredPayouts.map((payout) => (
                    <PayoutCard
                        key={payout.jobID}
                        payout={payout}
                        expanded={expandedId === payout.jobID}
                        onToggle={() => setExpandedId((prev) => (prev === payout.jobID ? null : payout.jobID))}
                    />
                ))}

                {filteredPayouts.length === 0 && !loading && (
                    <p className="text-center text-[12px] text-slate-500 mt-4 font-poppins">
                        No payouts found (On-Going / Completed).
                    </p>
                )}
            </div>

            {showStatementModal && (
                <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-[9998]">
                    <div className="bg-white rounded-2xl shadow-xl w-full max-w-md mx-4 p-4">
                        <div className="flex items-center justify-between mb-3">
                            <h3 className="text-[15px] font-semibold font-poppins text-slate-900">
                                Download Payout Statement
                            </h3>
                            <button
                                onClick={() => setShowStatementModal(false)}
                                className="p-1 rounded-full hover:bg-slate-100"
                            >
                                <svg className="w-4 h-4 text-slate-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>

                        <div className="space-y-3 text-[12px] font-poppins">
                            <div>
                                <label className="block text-slate-700 mb-1">Select Period</label>
                                <select
                                    className="w-full rounded-[10px] border border-slate-200 bg-white px-3 py-2 text-[12px] outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-400"
                                    value={statementType}
                                    onChange={(e) => setStatementType(e.target.value)}
                                >
                                    <option value="last10">Last 10 Days</option>
                                    <option value="last1m">Last 1 Month</option>
                                    <option value="last3m">Last 3 Months</option>
                                    <option value="last6m">Last 6 Months</option>
                                    <option value="overall">Overall</option>
                                    <option value="custom">Custom (From / To)</option>
                                </select>
                            </div>

                            {statementType === "custom" && (
                                <div className="grid grid-cols-2 gap-3">
                                    <div>
                                        <label className="block text-slate-700 mb-1">From Date</label>
                                        <input
                                            type="date"
                                            className="w-full rounded-[10px] border border-slate-200 bg-white px-3 py-2 text-[12px] outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-400"
                                            value={customFrom}
                                            onChange={(e) => setCustomFrom(e.target.value)}
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-slate-700 mb-1">To Date</label>
                                        <input
                                            type="date"
                                            className="w-full rounded-[10px] border border-slate-200 bg-white px-3 py-2 text-[12px] outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-400"
                                            value={customTo}
                                            onChange={(e) => setCustomTo(e.target.value)}
                                        />
                                    </div>
                                </div>
                            )}

                            <p className="text-[11px] text-slate-500 mt-1">
                                Statement will include only <b>On-Going</b> and <b>Completed</b> visits, and only their
                                processed/completed duty payout entries.
                            </p>
                        </div>

                        <div className="flex justify-end gap-2 mt-4">
                            <button
                                onClick={() => setShowStatementModal(false)}
                                className="px-3 py-1.5 rounded-md text-[12px] font-poppins bg-slate-100 text-slate-700 hover:bg-slate-200"
                                disabled={isGenerating}
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleDownloadStatement}
                                disabled={isGenerating}
                                className="px-3 py-1.5 rounded-md text-[12px] font-poppins bg-indigo-600 text-white hover:bg-indigo-700 disabled:bg-slate-400 disabled:cursor-not-allowed"
                            >
                                {isGenerating ? "Generating..." : "Download PDF"}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Payouts;